<script setup lang="ts">
import { ref } from 'vue'
import { ImageOff, ImagePlus, Upload } from '@lucide/vue'
import { cn } from '@/shared/lib/cn'

interface Props {
  previewUrl?: string | null
  loading?: boolean
  disabled?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  previewUrl: null,
  loading: false,
  disabled: false,
})
const emit = defineEmits<{
  click: []
  drop: [files: File[]]
}>()

const isDragging = ref(false)
let dragDepth = 0

function onDragEnter() {
  if (props.disabled) return
  dragDepth++
  isDragging.value = true
}

function onDragLeave() {
  dragDepth = Math.max(0, dragDepth - 1)
  if (dragDepth === 0) isDragging.value = false
}

function onDrop(e: DragEvent) {
  dragDepth = 0
  isDragging.value = false
  if (props.disabled) return
  const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : []
  emit('drop', files)
}
</script>

<template>
  <button
    type="button"
    :disabled="disabled || loading"
    :class="
      cn(
        'group relative flex flex-col items-center justify-center gap-2 overflow-hidden rounded-lg border-2 border-dashed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isDragging
          ? 'border-primary bg-primary/5'
          : previewUrl
            ? 'border-border hover:border-primary/40'
            : 'border-border hover:border-primary/40 bg-muted/40',
        (disabled || loading) && 'pointer-events-none opacity-60',
      )
    "
    @click="emit('click')"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <!-- Preview image -->
    <img
      v-if="previewUrl"
      :src="previewUrl"
      alt=""
      class="size-full object-cover"
      width="160"
      height="160"
      loading="lazy"
      decoding="async"
    />

    <!-- Placeholder (no image) -->
    <template v-else>
      <ImageOff
        class="size-7 text-muted-foreground transition-colors group-hover:text-foreground"
        aria-hidden="true"
      />
      <span class="text-xs text-muted-foreground transition-colors group-hover:text-foreground">
        Drop image or click to browse
      </span>
    </template>

    <!-- Drag overlay -->
    <div
      v-if="isDragging"
      class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/80 text-primary"
    >
      <Upload class="size-7" aria-hidden="true" />
      <span class="text-xs font-medium">Drop to upload</span>
    </div>

    <!-- Hover overlay when image is present -->
    <div
      v-if="previewUrl && !isDragging"
      class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
    >
      <ImagePlus class="size-6 text-white" aria-hidden="true" />
      <span class="text-xs font-medium text-white">Change image</span>
    </div>
  </button>
</template>
